// Sign-in step 2, only for someone with 2-step on: the 6-digit code from the authenticator app, or one recovery
// code instead (each works once). "Back to sign-in" starts again.
import { useState } from 'react'
import { ArrowLeft, KeyRound, ShieldCheck, Smartphone } from 'lucide-react'
import { OTPInput, notify } from '@the-kit/ui'
import type { User } from '@/api/schemas'
import { ErrorText } from '@/components/ErrorText'
import { useStableCallback } from '@/lib/useStableCallback'
import { useSignOut, useVerifyTwoStep } from '../useSession'
import { AuthLayout } from './AuthLayout'

export function CodeStep({ user }: { user: User }) {
  const verify = useVerifyTwoStep()
  const signOut = useSignOut()
  const [code, setCode] = useState('')
  const [recovery, setRecovery] = useState(false)
  const ready = recovery ? code.trim().length >= 8 : code.length === 6
  const submit = useStableCallback((typed: string = code) => {
    if (verify.isPending) return
    const input = recovery ? { recovery_code: typed.trim() } : { code: typed }
    verify.mutate(input, {
      onSuccess: () => {
        if (recovery) notify.warning('Recovery code used', 'Each works once. Make new ones on My account.')
      },
      onError: () => setCode(''),
    })
  })
  return (
    <AuthLayout title="2-step code" subtitle="Type the 6-digit code from your authenticator app">
      <div className="space-y-4" data-testid="two-step-form">
        <div className="card flex items-start gap-2.5 !p-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-primary text-white">
            <Smartphone size={14} />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-semibold text-default">Signing in as {user.name}</p>
            <p className="text-2xs text-muted">{user.email}</p>
          </div>
        </div>
        {recovery ? (
          <label className="block">
            <span className="label">Recovery code</span>
            <input
              className="input mono tracking-wider"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              autoComplete="off"
              autoFocus
              data-testid="recovery-input"
            />
            <span className="help">
              One of the codes you saved when you turned 2-step on. Each works once.
            </span>
          </label>
        ) : (
          <div className="flex justify-center py-3">
            <OTPInput value={code} onChange={setCode} onComplete={submit} error={!!verify.error} autoFocus />
          </div>
        )}
        <ErrorText error={verify.error} />
        <button
          type="button"
          className="btn btn-primary w-full"
          disabled={!ready || verify.isPending}
          onClick={() => submit()}
          data-testid="two-step-submit"
        >
          <ShieldCheck size={14} /> Sign in
        </button>
        <div className="flex items-center justify-between text-2xs">
          <button
            type="button"
            className="flex items-center gap-1 text-primary-text hover:underline"
            onClick={() => {
              setRecovery((on) => !on)
              setCode('')
              verify.reset()
            }}
            data-testid="use-recovery"
          >
            <KeyRound size={11} /> {recovery ? 'Use the authenticator code instead' : 'Use a recovery code'}
          </button>
          <button
            type="button"
            className="flex items-center gap-1 text-muted hover:text-default"
            onClick={() => signOut.mutate(undefined)}
          >
            <ArrowLeft size={11} /> Back to sign-in
          </button>
        </div>
      </div>
    </AuthLayout>
  )
}
